import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPrice, Input } from '@artosera/ui';
import { useSession } from '../../auth/useSession.js';
import {
  creerVersement,
  lireVueDAigle,
  listerBeneficiaires,
  listerVersements,
  modifierVersement,
} from '../../lib/api.js';
import type { BeneficiaireSaisie, VersementSaisie } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import {
  CelluleMontant,
  EtatOnglet,
  formaterJour,
  formaterMois,
  moisCourantParis,
  useLangue,
  useMessageErreur,
} from '../Finances/commun.js';
import { TableauSaisie, type ChampSaisie } from '../Finances/TableauSaisie.js';
import styles from './Associes.module.css';

/** Un bénéficiaire et son versement du mois, s'il est saisi. */
interface LigneVersement {
  id: string;
  beneficiaire: BeneficiaireSaisie;
  versement: VersementSaisie | null;
}

/**
 * Les versements du mois : le dû saisi, le versé, la date. À côté, en texte
 * atténué, « indicatif : part × marge » — un repère de lecture que la console
 * n'écrit JAMAIS d'elle-même : seul ce que la direction saisit est enregistré.
 */
export function VersementsAssocies(): JSX.Element {
  const { t } = useTranslation('console');
  const langue = useLangue();
  const message = useMessageErreur('versements');
  const { moi } = useSession();
  const compteId = moi?.compte.id ?? null;
  const client = useQueryClient();

  // La marge du mois vient de la vue d'aigle (même clé que la page d'accueil) :
  // son mois courant est celui de l'API, au fuseau de Paris.
  const vue = useQuery({
    queryKey: ['console', 'vue-d-aigle', compteId, '12m', 'defaut'],
    queryFn: () => lireVueDAigle('12m'),
  });
  const moisDeLaVue = vue.data?.revenus.mois ?? (vue.isLoading ? null : moisCourantParis());
  const [moisChoisi, setMoisChoisi] = useState<string | null>(null);
  const mois = moisChoisi ?? moisDeLaVue;

  const beneficiaires = useQuery({
    queryKey: ['console', 'partage', 'beneficiaires', compteId],
    queryFn: listerBeneficiaires,
  });
  const versements = useQuery({
    queryKey: ['console', 'partage', 'versements', compteId, mois],
    queryFn: () => listerVersements(mois ?? undefined),
    enabled: mois !== null,
  });

  const relire = async (): Promise<void> => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['console', 'partage', 'versements'] }),
      client.invalidateQueries({ queryKey: ['console', 'vue-d-aigle'] }),
    ]);
  };
  const creer = useMutation({ mutationFn: creerVersement, onSuccess: relire });
  const modifier = useMutation({
    mutationFn: ({ id, corps }: { id: string; corps: Record<string, unknown> }) =>
      modifierVersement(id, corps),
    onSuccess: relire,
  });

  const lignes: LigneVersement[] = (beneficiaires.data ?? []).map((beneficiaire) => ({
    id: beneficiaire.id,
    beneficiaire,
    versement:
      (versements.data?.items ?? []).find(
        (versement) => versement.beneficiaireId === beneficiaire.id,
      ) ?? null,
  }));
  // Un mois clôturé garde le dû que sa clôture a écrit : l'API refuse de le
  // changer, ou d'en ajouter un. Seuls le versé et sa date se saisissent, sur
  // les versements que la clôture a posés.
  const moisClos = mois !== null && (versements.data?.moisClotures ?? []).includes(mois);

  // Part × marge, à titre INDICATIF : seulement quand la marge du mois choisi
  // est connue — un mois sans coût saisi n'a pas de marge.
  const marge =
    vue.data && vue.data.revenus.mois === mois ? vue.data.indicateurs.margeMoisCentimes : null;
  const indicatif = (ligne: LigneVersement): JSX.Element => {
    const part = ligne.beneficiaire.partPointsDeBase;
    if (part === null || marge === null) {
      return <span className={styles.indicatif}>{t('associes.versements.indicatifAbsent')}</span>;
    }
    const centimes = Math.round((part * marge) / 10_000);
    return (
      <span className={styles.indicatif}>
        {t('associes.versements.indicatif', { montant: formatPrice(centimes, langue) })}
      </span>
    );
  };

  const champs: ChampSaisie[] = [
    ...(moisClos
      ? []
      : [
          {
            cle: 'montantDuCentimes',
            titre: t('associes.versements.champs.du'),
            type: 'montant' as const,
          },
        ]),
    {
      cle: 'verseCentimes',
      titre: t('associes.versements.champs.verse'),
      type: 'montant',
      videVaut: 0,
    },
    { cle: 'verseLe', titre: t('associes.versements.champs.verseLe'), type: 'date' },
  ];

  const attente = mois === null || beneficiaires.isLoading || versements.isLoading;
  const enErreur = beneficiaires.isError || versements.isError;

  return (
    <section className={pageStyles.panneau} aria-labelledby="associes-versements">
      <div className={`${pageStyles.sectionTitre} ${styles.titreEtMois}`}>
        <h2 id="associes-versements">{t('associes.versements.titre')}</h2>
        <div className={styles.choixMois}>
          <label htmlFor="versements-mois">{t('associes.versements.mois')}</label>
          <Input
            id="versements-mois"
            mono
            type="month"
            placeholder="AAAA-MM"
            value={mois ?? ''}
            onChange={(e) => {
              if (/^\d{4}-(0[1-9]|1[0-2])$/.test(e.target.value)) setMoisChoisi(e.target.value);
            }}
          />
        </div>
      </div>

      {attente || enErreur || mois === null ? (
        <EtatOnglet chargement={attente} enErreur={enErreur} />
      ) : (
        <TableauSaisie<LigneVersement>
          legende={t('associes.versements.titre')}
          introduction={
            moisClos ? t('associes.versements.moisCloture') : t('associes.versements.texte')
          }
          lignes={lignes}
          colonnes={[
            {
              cle: 'beneficiaire',
              titre: t('associes.versements.colonnes.beneficiaire'),
              rendu: (ligne) => ligne.beneficiaire.libelle,
            },
            {
              cle: 'du',
              titre: t('associes.versements.colonnes.du'),
              numerique: true,
              rendu: (ligne) => (
                <CelluleMontant centimes={ligne.versement?.montantDuCentimes ?? null} />
              ),
            },
            {
              cle: 'verse',
              titre: t('associes.versements.colonnes.verse'),
              numerique: true,
              rendu: (ligne) =>
                ligne.versement ? <CelluleMontant centimes={ligne.versement.verseCentimes} /> : '—',
            },
            {
              cle: 'verseLe',
              titre: t('associes.versements.colonnes.verseLe'),
              numerique: true,
              rendu: (ligne) => formaterJour(ligne.versement?.verseLe ?? null, langue),
            },
            {
              cle: 'indicatif',
              titre: t('associes.versements.colonnes.indicatif'),
              rendu: indicatif,
            },
          ]}
          champs={champs}
          modificationPossible={(ligne) => !moisClos || ligne.versement !== null}
          valeursDe={(ligne) => ({
            montantDuCentimes: ligne.versement?.montantDuCentimes ?? null,
            verseCentimes: ligne.versement?.verseCentimes ?? 0,
            verseLe: ligne.versement?.verseLe ?? null,
          })}
          libelleLigne={(ligne) => `${ligne.beneficiaire.libelle} · ${formaterMois(mois, langue)}`}
          libelleModifier={(ligne) =>
            ligne.versement ? t('saisies.modifier') : t('associes.versements.saisir')
          }
          enEcriture
          libelleAjouter=""
          messageVide={t('associes.versements.vide')}
          // Les lignes existent déjà, une par bénéficiaire : on les saisit, on n'en ajoute pas.
          surModifier={async (ligne, changements, complet) => {
            if (ligne.versement) {
              await modifier.mutateAsync({ id: ligne.versement.id, corps: changements });
            } else {
              await creer.mutateAsync({
                beneficiaireId: ligne.beneficiaire.id,
                mois,
                ...complet,
              });
            }
          }}
          message={message}
        />
      )}
    </section>
  );
}
